import moment from 'moment';

export type DmsType = 'lat' | 'long';

export class FormatterUtils {
  static formatDateTime(value: Date | number | string) {
    if (!value) {
      return null;
    }

    if (isInvalidDate(value as any)) {
      return (value as Date).toString();
    }

    const aux = moment(value).format('DD/MM/YYYY HH:mm:ss');
    return aux;
  }

  static formatPoint(point: { x: number; y: number }) {
    if (!point) {
      return '';
    }

    return [
      FormatterUtils.formatNumber(point.y),
      FormatterUtils.formatNumber(point.x),
    ].join(', ');
  }

  static formatNumber(value: number, maximumFractionDigits?: number) {
    if (typeof value !== 'number') {
      return null;
    }

    const formatter = new Intl.NumberFormat('en-IN', {
      maximumFractionDigits: maximumFractionDigits ?? 6,
      useGrouping: false,
    });
    return formatter.format(value);
  }

  static formatDD(value: number, type: DmsType, hemisphereMode = true) {
    const { absDD, hemisphere, prefix } = calcDegrees(value, type);

    const absDDFormatted = FormatterUtils.formatNumber(absDD);

    const aux = `${absDDFormatted}°`;
    if (hemisphereMode) {
      return `${aux} ${hemisphere}`;
    } else {
      return `${prefix}${aux}`;
    }
  }

  static formatDDM(value: number, type: DmsType, hemisphereMode = true) {
    const { degrees, minutes, hemisphere, prefix } = calcDegrees(value, type);

    const aux = `${degrees}° ${minutes}′`;
    if (hemisphereMode) {
      return `${aux} ${hemisphere}`;
    } else {
      return `${prefix}${aux}`;
    }
  }

  static formatDMS(value: number, type: DmsType, hemisphereMode = true) {
    const { degrees, minutes, seconds, hemisphere, prefix } = calcDegrees(
      value,
      type
    );

    const secondsFormatted = FormatterUtils.formatNumber(seconds);

    const aux = `${degrees}° ${minutes}′ ${secondsFormatted}″`;
    if (hemisphereMode) {
      return `${aux} ${hemisphere}`;
    } else {
      return `${prefix}${aux}`;
    }
  }
}

function isInvalidDate(date: Date) {
  return date instanceof Date && isNaN(date.getTime());
}

function truncate(n: number) {
  return n > 0 ? Math.floor(n) : Math.ceil(n);
}

function calcDegrees(value: number, type: DmsType) {
  const hemisphere = /^[WE]|(?:lon)/i.test(type)
    ? value < 0
      ? 'W'
      : 'E'
    : value < 0
    ? 'S'
    : 'N';

  const prefix = value > 0 ? '+' : '-';

  const absDD = Math.abs(value);
  const degrees = truncate(absDD);
  const minutes = truncate((absDD - degrees) * 60);
  const seconds = (absDD - degrees - minutes / 60) * 3600;

  return {
    absDD,
    degrees,
    minutes,
    seconds,
    hemisphere,
    prefix,
  };
}
